import { useMemo, useRef, useState, type ReactNode } from 'react';

import { cx } from '../lib/cx';
import { useOnClickOutside, useOnEscape } from '../lib/hooks';

import { Checkbox } from './Checkbox';

export interface Column<Row> {
  id: string;
  header: string;
  /** Cell renderer. */
  render: (row: Row) => ReactNode;
  /** When set, the column is sortable and this extracts the comparison value. */
  sortValue?: (row: Row) => string | number;
  /** Hide from the column chooser and never removable (e.g. the primary column). */
  required?: boolean;
}

export interface RowMenuItem {
  label: string;
  onSelect: () => void;
  destructive?: boolean;
}

export type SortDir = 1 | -1;
export interface SortState {
  columnId: string;
  dir: SortDir;
}

export interface DataTableProps<Row> {
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  getRowId: (row: Row) => string;
  /** Accessible table name. */
  label: string;
  /** CSS grid-template-columns for header + rows (design-reference §6.5). */
  gridTemplate: string;
  /** Initial sort (uncontrolled). */
  initialSort?: SortState;
  /** Enable the leading select column + selection bar. */
  selectable?: boolean;
  selectedIds?: readonly string[];
  onSelectionChange?: (ids: string[]) => void;
  /** Rendered inside the selection bar when rows are selected. */
  selectionActions?: ReactNode;
  /** Per-row overflow (kebab) menu. */
  rowMenu?: (row: Row) => readonly RowMenuItem[];
  /** Row click (opens the record). Checkbox/menu clicks never trigger it. */
  onRowClick?: (row: Row) => void;
  /** Sticky header (Documents only). */
  stickyHeader?: boolean;
  /** Offer the column chooser. */
  columnChooser?: boolean;
  /** Footer content (counts + pager). */
  footer?: ReactNode;
  /** Empty-state node when there are no rows. */
  empty?: ReactNode;
}

/**
 * A headless-enough data table: sortable columns, a column chooser, sticky
 * header, row hover, multi-select with a selection bar, per-row overflow menu,
 * and a footer slot for pagination. Rendered as an ARIA grid of divs (a11y
 * §8.6.8) — real `columnheader`s with `aria-sort`, a labelled table, and the
 * primary cell a real click target rather than a whole-row div.
 *
 * Saved-views hook: sort + visible-column state is exposed via `initialSort` and
 * the column chooser; a screen persists the current view by controlling those.
 */
export function DataTable<Row>({
  columns,
  rows,
  getRowId,
  label,
  gridTemplate,
  initialSort,
  selectable = false,
  selectedIds,
  onSelectionChange,
  selectionActions,
  rowMenu,
  onRowClick,
  stickyHeader = false,
  columnChooser = false,
  footer,
  empty,
}: DataTableProps<Row>): React.JSX.Element {
  const [sort, setSort] = useState<SortState | null>(initialSort ?? null);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const visibleColumns = columns.filter((c) => !hidden.has(c.id));

  const sorted = useMemo(() => {
    if (sort === null) return rows;
    const column = columns.find((c) => c.id === sort.columnId);
    if (column?.sortValue === undefined) return rows;
    const getValue = column.sortValue;
    return [...rows].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (av < bv) return -1 * sort.dir;
      if (av > bv) return 1 * sort.dir;
      return 0;
    });
  }, [rows, sort, columns]);

  const selected = useMemo(() => new Set(selectedIds ?? []), [selectedIds]);
  const allSelected = sorted.length > 0 && sorted.every((r) => selected.has(getRowId(r)));
  const someSelected = sorted.some((r) => selected.has(getRowId(r)));

  const toggleSort = (columnId: string): void => {
    setSort((current) => {
      if (current?.columnId === columnId) return { columnId, dir: current.dir === 1 ? -1 : 1 };
      return { columnId, dir: 1 };
    });
  };

  const toggleRow = (id: string): void => {
    if (onSelectionChange === undefined) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange([...next]);
  };

  const toggleAll = (): void => {
    if (onSelectionChange === undefined) return;
    onSelectionChange(allSelected ? [] : sorted.map(getRowId));
  };

  const gridStyle = { gridTemplateColumns: (selectable ? '36px ' : '') + gridTemplate + (rowMenu ? ' 44px' : '') };

  return (
    <div className="ds-card ds-table" role="table" aria-label={label} aria-rowcount={sorted.length}>
      {(selectable && someSelected) || columnChooser ? (
        <div className="ds-table__selbar" style={{ background: someSelected ? undefined : 'var(--card)' }}>
          {selectable && someSelected ? (
            <>
              <span className="ds-table__selcount">{selected.size} selected</span>
              {selectionActions}
              <button
                type="button"
                className="ds-linkbtn"
                style={{ marginLeft: 'auto' }}
                onClick={() => {
                  onSelectionChange?.([]);
                }}
              >
                Clear selection
              </button>
            </>
          ) : null}
          {columnChooser && !someSelected ? (
            <ColumnChooser columns={columns} hidden={hidden} onToggle={setHidden} />
          ) : null}
        </div>
      ) : null}

      <div className="ds-table__scroll">
        <div
          className={cx('ds-table__grid ds-table__head', stickyHeader && 'ds-table__head--sticky')}
          role="row"
          style={gridStyle}
        >
          {selectable ? (
            <span role="columnheader">
              <Checkbox
                aria-label="Select all rows"
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={toggleAll}
              />
            </span>
          ) : null}
          {visibleColumns.map((column) => {
            const isSorted = sort?.columnId === column.id;
            const ariaSort = isSorted ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none';
            return (
              <span
                key={column.id}
                role="columnheader"
                className="ds-table__cell"
                aria-sort={column.sortValue ? ariaSort : undefined}
              >
                {column.sortValue ? (
                  <button type="button" className="ds-th ds-th--sortable" onClick={() => { toggleSort(column.id); }}>
                    {column.header}
                    {isSorted ? <span aria-hidden="true">{sort.dir === 1 ? ' ↑' : ' ↓'}</span> : null}
                  </button>
                ) : (
                  <span className="ds-th">{column.header}</span>
                )}
              </span>
            );
          })}
          {rowMenu ? <span role="columnheader" className="sr-only">Actions</span> : null}
        </div>

        {sorted.length === 0 ? (
          <div role="row">
            <span role="cell">{empty}</span>
          </div>
        ) : (
          sorted.map((row) => {
            const id = getRowId(row);
            return (
              <div
                key={id}
                role="row"
                className={cx('ds-table__grid ds-table__row', onRowClick && 'ds-table__row--clickable')}
                aria-selected={selectable ? selected.has(id) : undefined}
                style={gridStyle}
              >
                {selectable ? (
                  <span role="cell">
                    <Checkbox
                      aria-label={`Select row ${id}`}
                      checked={selected.has(id)}
                      onChange={() => { toggleRow(id); }}
                    />
                  </span>
                ) : null}
                {visibleColumns.map((column, index) => (
                  <span role="cell" className="ds-table__cell" key={column.id}>
                    {index === 0 && onRowClick !== undefined ? (
                      <button
                        type="button"
                        className="ds-linkbtn"
                        style={{ color: 'inherit', textAlign: 'left' }}
                        onClick={() => { onRowClick(row); }}
                      >
                        {column.render(row)}
                      </button>
                    ) : (
                      column.render(row)
                    )}
                  </span>
                ))}
                {rowMenu ? (
                  <span role="cell">
                    <RowMenu items={rowMenu(row)} rowId={id} />
                  </span>
                ) : null}
              </div>
            );
          })
        )}
      </div>

      {footer}
    </div>
  );
}

function ColumnChooser<Row>({
  columns,
  hidden,
  onToggle,
}: {
  columns: readonly Column<Row>[];
  hidden: ReadonlySet<string>;
  onToggle: (next: ReadonlySet<string>) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, open, () => { setOpen(false); });
  useOnEscape(open, () => { setOpen(false); });

  return (
    <div className="ds-colmenu" ref={ref}>
      <button
        type="button"
        className="ds-linkbtn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => { setOpen((o) => !o); }}
      >
        Columns
      </button>
      {open ? (
        <ul className="ds-menu" role="menu" aria-label="Choose columns">
          {columns.map((column) => {
            const visible = !hidden.has(column.id);
            return (
              <li key={column.id}>
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={visible}
                  className="ds-menu__item"
                  disabled={column.required}
                  onClick={() => {
                    const next = new Set(hidden);
                    if (visible) next.add(column.id);
                    else next.delete(column.id);
                    onToggle(next);
                  }}
                >
                  <span aria-hidden="true">{visible ? '☑' : '☐'}</span>
                  {column.header}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function RowMenu({ items, rowId }: { items: readonly RowMenuItem[]; rowId: string }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, open, () => { setOpen(false); });
  useOnEscape(open, () => { setOpen(false); });

  return (
    <div className="ds-colmenu" ref={ref} style={{ marginLeft: 0 }}>
      <button
        type="button"
        className="ds-iconbtn ds-iconbtn--bare"
        aria-label={`Actions for ${rowId}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => { setOpen((o) => !o); }}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open ? (
        <ul className="ds-menu" role="menu" aria-label="Row actions">
          {items.map((item) => (
            <li key={item.label}>
              <button
                type="button"
                role="menuitem"
                className="ds-menu__item"
                style={item.destructive ? { color: 'var(--bad)' } : undefined}
                onClick={() => {
                  item.onSelect();
                  setOpen(false);
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
